import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ModeSwitcher from './ModeSwitcher';
import api, { notificationAPI, billingAPI } from '../services/api';
import PaymentSheet from './PaymentSheet';
import { setTenantTZ, fmtDatetime } from '../utils/dateUtils';
import {
  LayoutDashboard,
  Wallet,
  Package,
  Factory,
  ShoppingCart,
  BarChart3,
  Bot,
  Brain,
  Users,
  FileText,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Bell,
  Search,
  Settings,
  LogOut,
  Warehouse as WarehouseIcon,
  Truck,
  ClipboardList,
  Globe,
  Upload,
  Lock, Key,
  Zap,
  Check,
  ChefHat, UtensilsCrossed, Monitor, Shirt, Scissors, RotateCcw, Wrench, Store,
  MessageCircle,
} from 'lucide-react';


const TIER_REQUIRED = {
  multi_warehouse: 'plus',
  bulk_import: 'plus',
  channel_manager_full: 'plus',
  ai_insights_full: 'max',
  multi_user_3: 'standard',
};

const TIER_LABEL = { trial: 'Trial', standard: 'Standard', plus: 'Plus', max: 'Max' };

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/', allowedRoles: ['*'], feature: 'dashboard' },
  { id: 'sales', label: 'Penjualan', icon: ShoppingCart, path: '/sales', allowedRoles: ['owner', 'admin'], feature: 'sales', children: [
    { label: 'Pesanan', path: '/sales' },
    { label: 'Surat Jalan', path: '/delivery-orders' },
    { label: 'Loyalty Program', path: '/loyalty' },
  ]},
  { id: 'financial', label: 'Keuangan', icon: Wallet, path: '/financial', allowedRoles: ['owner', 'admin', 'finance'], feature: 'financial_management', children: [
    { label: 'Ringkasan', path: '/financial' },
    { label: 'Jurnal', path: '/financial/journal' },
    { label: 'Laba Rugi', path: '/financial/profit-loss' },
    { label: 'Neraca', path: '/financial/neraca' },
    { label: 'Arus Kas', path: '/financial/cashflow' },
    { label: 'Piutang', path: '/financial/receivables' },
    { label: 'Hutang', path: '/financial/payables' },
    { label: 'Aset Tetap', path: '/fixed-assets' },
    { label: 'Penggajian', path: '/payroll' },
  ]},
  { id: 'inventory', label: 'Inventori', icon: Package, path: '/inventory', allowedRoles: ['owner', 'admin', 'warehouse', 'production'], feature: 'inventory', children: [
    { label: 'Barang Jadi', path: '/inventory/products' },
    { label: 'Stok Rendah', path: '/inventory/low-stock' },
    { label: 'Stok Habis', path: '/inventory/out-stock' },
    { label: 'Bahan Baku', path: '/inventory/materials' },
    { label: 'Supplier', path: '/inventory/suppliers' },
    { label: 'Barang Masuk', path: '/inventory/stock-in' },
    { label: 'Barang Keluar', path: '/inventory/stock-out' },
    { label: 'Pergerakan Stok', path: '/inventory/movements' },
    { label: 'Multi-Gudang', path: '/warehouses' },
  ]},
  { id: 'operations', label: 'Operasi', icon: Factory, path: '/purchase-orders', allowedRoles: ['owner', 'admin', 'warehouse', 'production'], feature: 'purchase_orders', children: [
    { label: 'Pembelian (PO)', path: '/purchase-orders' },
    { label: 'Produksi', path: '/production' },
    { label: 'Bulk Import', path: '/import', feature: 'bulk_import' },
  ]},
  { id: 'reports', label: 'Laporan', icon: BarChart3, path: '/reports', allowedRoles: ['owner', 'admin', 'finance'], feature: 'reports_export' },
  { id: 'insight', label: 'Insight', icon: BarChart3, path: '/analytics', allowedRoles: ['owner', 'admin'], feature: 'analytics_basic', children: [
    { label: 'Analitik', path: '/analytics' },
    { label: 'AI Insights', path: '/ai-insights' },
    { label: 'Channel Manager', path: '/channels' },
  ]},
  { id: 'whatsapp_ai', label: 'WhatsApp & AI', icon: MessageCircle, path: '/whatsapp', allowedRoles: ['*'], feature: 'dashboard', children: [
    { label: 'WhatsApp & AI', path: '/whatsapp' },
    { label: 'Knowledge Base AI', path: '/knowledge-base' },
  ]},
  { id: 'settings', label: 'Pengaturan', icon: Settings, path: '/settings', allowedRoles: ['owner', 'admin'], feature: 'dashboard', children: [
    { label: 'Profil & Perusahaan', path: '/settings' },
    { label: 'WhatsApp & AI', path: '/whatsapp', feature: 'dashboard' },
    { label: 'Pengguna', path: '/users', feature: 'multi_user_3' },
    { label: 'API Keys', path: '/api-keys', feature: 'api_key_generator' },
  ]},
];

const INDUSTRY_MENUS = {
  fnb: [
    { id: 'fnb_kitchen', label: 'Dapur & Resep', icon: ChefHat, path: '/fnb/recipes', allowedRoles: ['owner', 'admin', 'production'], feature: 'recipe_card', children: [
      { label: 'Manajemen Resep', path: '/fnb/recipes' },
      { label: 'Bahan Baku & Konversi', path: '/fnb/ingredients' },
      { label: 'Estimasi Kebutuhan/Hari', path: '/fnb/ingredient-estimates' },
      { label: 'Expiry Tracking', path: '/fnb/expiry' },
      { label: 'Waste Tracking', path: '/fnb/waste' },
    ]},
    { id: 'fnb_menu', label: 'Menu & Promo', icon: UtensilsCrossed, path: '/fnb/menu', allowedRoles: ['owner', 'admin'], feature: 'menu_management', children: [
      { label: 'Manajemen Menu', path: '/fnb/menu' },
      { label: 'Modifier Groups', path: '/fnb/modifiers' },
      { label: 'Promo & Diskon', path: '/fnb/promos' },
      { label: 'Menu Musiman', path: '/fnb/seasonal' },
      { label: 'Paket Bundling', path: '/fnb/bundles' },
    ]},
    { id: 'fnb_ops', label: 'Operasional Resto', icon: Store, path: '/fnb/tables', allowedRoles: ['owner', 'admin'], feature: 'table_management', children: [
      { label: 'Manajemen Meja', path: '/fnb/tables' },
      { label: 'Reservasi', path: '/fnb/reservations' },
      { label: 'Manajemen Shift', path: '/fnb/shifts' },
      { label: 'QR Order', path: '/fnb/qr-order' },
      { label: 'Kitchen Display (KDS)', path: '/fnb/kds' },
    ]},
    { id: 'fnb_pos', label: 'POS Kasir FnB', icon: Monitor, path: '/fnb/pos', allowedRoles: ['owner', 'admin'], feature: 'pos_fnb' },
    { id: 'fnb_delivery', label: 'Pesanan Online', icon: Truck, path: '/fnb/delivery', allowedRoles: ['owner', 'admin'], feature: 'online_delivery_order', children: [
      { label: 'Pesanan Delivery', path: '/fnb/delivery' },
      { label: 'GoFood / GrabFood', path: '/fnb/platforms' },
      { label: 'Pre-order Katering', path: '/fnb/catering' },
    ]},
  ],
  fashion: [
    { id: 'fashion_variants', label: 'Produk & Variant', icon: Shirt, path: '/fashion/variants', allowedRoles: ['owner', 'admin', 'warehouse'], feature: 'variant_matrix', children: [
      { label: 'Variant Matrix', path: '/fashion/variants' },
      { label: 'SKU Per Variant', path: '/fashion/sku-variants' },
      { label: 'Stok Per Variant', path: '/fashion/stock-variants' },
      { label: 'Cetak Hangtag', path: '/fashion/hangtag' },
      { label: 'Stock Opname', path: '/fashion/opname' },
    ]},
    { id: 'fashion_production', label: 'Produksi Fashion', icon: Scissors, path: '/fashion/bom', allowedRoles: ['owner', 'admin', 'production'], feature: 'bom_fashion', children: [
      { label: 'BOM Garmen', path: '/fashion/bom' },
      { label: 'Cutting Order', path: '/fashion/cutting' },
      { label: 'Work Order Jahit', path: '/fashion/work-orders' },
      { label: 'HPP Per SKU', path: '/fashion/hpp' },
      { label: 'Pre-order & Indent', path: '/fashion/preorder' },
    ]},
    { id: 'fashion_pos', label: 'POS Toko Fisik', icon: Monitor, path: '/fashion/pos', allowedRoles: ['owner', 'admin'], feature: 'pos_fashion', children: [
      { label: 'Kasir Toko', path: '/fashion/pos' },
      { label: 'Konsinyasi', path: '/fashion/consignment' },
      { label: 'Tracking Sales Rep', path: '/fashion/sales-reps' },
    ]},
    { id: 'fashion_marketplace', label: 'Marketplace & Online', icon: Globe, path: '/fashion/marketplace', allowedRoles: ['owner', 'admin'], feature: 'marketplace_sync', children: [
      { label: 'Sinkronisasi Stok', path: '/fashion/marketplace' },
      { label: 'Manajemen Pesanan', path: '/fashion/orders' },
      { label: 'Manajemen Reseller', path: '/fashion/resellers' },
      { label: 'Katalog Digital', path: '/fashion/catalog' },
    ]},
    { id: 'fashion_returns', label: 'Retur & Pengiriman', icon: RotateCcw, path: '/fashion/returns', allowedRoles: ['owner', 'admin', 'warehouse'], feature: 'return_customer', children: [
      { label: 'Retur dari Customer', path: '/fashion/returns/customer' },
      { label: 'Retur ke Supplier', path: '/fashion/returns/supplier' },
      { label: 'Integrasi Kurir', path: '/fashion/courier' },
    ]},
  ],
  manufaktur: [
    { id: 'mfg_production', label: 'Produksi', icon: Factory, path: '/mfg/work-orders', allowedRoles: ['owner', 'admin', 'production'], feature: 'work_order', children: [
      { label: 'Work Order', path: '/mfg/work-orders' },
      { label: 'Production Planning', path: '/mfg/planning' },
      { label: 'BOM Multi-Level', path: '/mfg/bom' },
      { label: 'Aktual vs Target', path: '/mfg/actual-vs-target' },
      { label: 'Scrap Tracking', path: '/mfg/scrap' },
      { label: 'Kapasitas Mesin', path: '/mfg/capacity' },
      { label: 'Labor Tracking', path: '/mfg/labor' },
    ]},
    { id: 'mfg_quality', label: 'Kualitas (QC)', icon: ClipboardList, path: '/mfg/quality', allowedRoles: ['owner', 'admin', 'production'], feature: 'quality_control', children: [
      { label: 'QC Checklist', path: '/mfg/quality' },
      { label: 'Reject Tracking', path: '/mfg/rejects' },
      { label: 'Compliance Docs', path: '/mfg/compliance' },
    ]},
    { id: 'mfg_assets', label: 'Mesin & Aset', icon: Wrench, path: '/mfg/assets', allowedRoles: ['owner', 'admin'], feature: 'asset_register', children: [
      { label: 'Asset Register', path: '/mfg/assets' },
      { label: 'Jadwal Maintenance', path: '/mfg/maintenance' },
      { label: 'Downtime Tracking', path: '/mfg/downtime' },
    ]},
    { id: 'mfg_b2b', label: 'Penjualan B2B', icon: FileText, path: '/mfg/quotations', allowedRoles: ['owner', 'admin', 'finance'], feature: 'quotation_b2b', children: [
      { label: 'Quotation / Penawaran', path: '/mfg/quotations' },
      { label: 'Sales Order B2B', path: '/mfg/sales-orders' },
      { label: 'Invoice & Faktur', path: '/mfg/invoices' },
      { label: 'Kontrak Pelanggan', path: '/mfg/contracts' },
    ]},
    { id: 'mfg_purchase', label: 'Pembelian Bahan', icon: Package, path: '/mfg/goods-receipt', allowedRoles: ['owner', 'admin', 'warehouse'], feature: 'goods_receipt', children: [
      { label: 'PO Bahan Baku', path: '/mfg/po-bahan' },
      { label: 'Penerimaan Barang (GRN)', path: '/mfg/goods-receipt' },
      { label: 'Evaluasi Supplier', path: '/mfg/supplier-eval' },
    ]},
  ],
  retail: [
    { id: 'retail_pos', label: 'POS & Kasir', icon: Monitor, path: '/retail/pos', allowedRoles: ['owner', 'admin'], feature: 'pos_retail', children: [
      { label: 'Kasir / POS', path: '/retail/pos' },
      { label: 'Tutup Kasir', path: '/retail/cashier-closing' },
      { label: 'Multi-Toko / Cabang', path: '/retail/stores' },
    ]},
    { id: 'retail_customer', label: 'Pelanggan & Member', icon: Users, path: '/retail/customers', allowedRoles: ['owner', 'admin'], feature: 'customer_database', children: [
      { label: 'Database Pelanggan', path: '/retail/customers' },
      { label: 'Riwayat Pembelian', path: '/retail/purchase-history' },
      { label: 'Loyalty Program', path: '/retail/loyalty' },
      { label: 'Promo Member', path: '/retail/member-promos' },
    ]},
    { id: 'retail_stock', label: 'Stok Toko', icon: Package, path: '/retail/opname', allowedRoles: ['owner', 'admin', 'warehouse'], feature: 'stock_opname_retail', children: [
      { label: 'Stock Opname', path: '/retail/opname' },
      { label: 'Price Tag Printing', path: '/retail/price-tags' },
      { label: 'Multi-Lokasi / Rak', path: '/retail/locations' },
      { label: 'Reorder Otomatis', path: '/retail/reorder' },
    ]},
    { id: 'retail_online', label: 'Toko Online', icon: Globe, path: '/retail/marketplace', allowedRoles: ['owner', 'admin'], feature: 'marketplace_retail', children: [
      { label: 'Integrasi Marketplace', path: '/retail/marketplace' },
      { label: 'Manajemen Pesanan Online', path: '/retail/online-orders' },
      { label: 'Sinkronisasi Online⇔Offline', path: '/retail/omnichannel' },
    ]},
  ],
};

const WORKSPACES = [
  { id: 'all', label: 'Ringkasan', hint: 'Umum', icon: LayoutDashboard },
  { id: 'fnb', label: 'FnB', hint: 'Resto & cafe', icon: ChefHat },
  { id: 'fashion', label: 'Fashion', hint: 'Apparel', icon: Shirt },
  { id: 'retail', label: 'Retail', hint: 'Toko', icon: Store },
  { id: 'manufaktur', label: 'Manufaktur', hint: 'Produksi', icon: Factory },
];

const normalizeIndustry = (value = '') => {
  const unit = String(value || '').trim().toLowerCase();
  if (['mfg', 'manufacturing', 'manufacture', 'manufactory'].includes(unit)) return 'manufaktur';
  if (['restaurant', 'resto', 'food', 'food_beverage', 'food & beverage'].includes(unit)) return 'fnb';
  if (['apparel', 'garment', 'clothing'].includes(unit)) return 'fashion';
  if (['store', 'shop', 'general_retail'].includes(unit)) return 'retail';
  if (INDUSTRY_MENUS[unit]) return unit;
  return unit || 'all';
};

const workspaceFromPath = (path = '') => {
  if (path.startsWith('/fnb')) return 'fnb';
  if (path.startsWith('/fashion')) return 'fashion';
  if (path.startsWith('/retail')) return 'retail';
  if (path.startsWith('/mfg')) return 'manufaktur';
  return '';
};

const isTestingAccount = (user) => {
  return user?.is_super_admin || !!user?.is_test_account;
};

const canSeeItem = (item, user) => {
  if (!item || !user) return false;
  if (isTestingAccount(user)) return true;
  if (item.allowedRoles?.includes('*')) return true;
  return item.allowedRoles?.includes(user.role);
};

const visibleFor = (user, industry, workspace = 'all') => {
  if (!user) return [];
  const tenantIndustry = normalizeIndustry(industry || user.industry || user.business_type);
  const selectedIndustry = isTestingAccount(user)
    ? normalizeIndustry(workspace || tenantIndustry)
    : tenantIndustry;
  const industryMenus = selectedIndustry !== 'all' ? (INDUSTRY_MENUS[selectedIndustry] || []) : [];
  const allItems = [
    ...menuItems.slice(0, 4),
    ...industryMenus,
    ...menuItems.slice(4),
  ];
  return allItems.filter((item) => canSeeItem(item, user));
};

// ─── Trial Banner ────────────────────────────────────────────────
const TrialBanner = ({ days, onUpgrade, onDismiss }) => {
  if (days === null || days === undefined || days > 7) return null;
  let bg, border, text, icon;
  if (days <= 1) {
    bg = 'bg-red-50'; border = 'border-red-200'; text = 'text-red-700'; icon = '🚨';
  } else if (days <= 3) {
    bg = 'bg-orange-50'; border = 'border-orange-200'; text = 'text-orange-700'; icon = '⚠️';
  } else {
    bg = 'bg-amber-50'; border = 'border-amber-200'; text = 'text-amber-700'; icon = '⚠️';
  }
  const msg = days <= 1
    ? 'Trial berakhir besok! Upgrade sekarang untuk tetap akses semua fitur'
    : days <= 3
    ? `Trial kamu tersisa ${days} hari lagi. Jangan sampai data terkunci!`
    : `Trial kamu tersisa ${days} hari lagi. Upgrade sekarang →`;

  return (
    <div className={`flex items-center justify-between px-4 py-2.5 border-b ${bg} ${border}`}>
      <p className={`text-sm ${text} font-semibold`}>
        {days <= 1
          ? 'Trial: berakhir besok. Upgrade sekarang agar akses tetap aktif.'
          : days <= 3
          ? `Trial: tersisa ${days} hari. Amankan akses sebelum berakhir.`
          : `Trial: tersisa ${days} hari. Upgrade kapan pun saat siap.`}
      </p>
      <div className="flex items-center gap-2 ml-4 flex-shrink-0">
        <button
          onClick={onUpgrade}
          className={`text-xs px-3 py-1.5 rounded-lg border font-semibold transition-all ${text} border-current hover:opacity-80`}
        >
          Upgrade Sekarang
        </button>
        <button onClick={onDismiss} className={`${text} hover:opacity-60`}>
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// ─── Data fitur & kredit per paket (dipakai UpgradeModal) ──────────
const PLAN_FEATURES = {
  standard: {
    tagline: 'Asisten',
    price: '99.000',
    credits: 2000,
    features: ['Manajemen Inventori', 'Laporan Keuangan', '1 Channel Marketplace', '3 Pengguna · 500 SKU', 'Chat Bisnis: baca data + catat transaksi', 'Invoice & nota otomatis'],
  },
  plus: {
    tagline: 'Kreator',
    price: '199.000',
    credits: 6000,
    features: ['Semua fitur Standard +', 'Semua Channel Marketplace', 'Multi-Gudang · Bulk Import', '10 Pengguna · 2.000 SKU', 'AI bikinkan Excel, Word, PPT', 'Baca foto nota otomatis', 'Ruangan tim tanpa batas'],
  },
  max: {
    tagline: 'Otomatis',
    price: '270.000',
    credits: 15000,
    features: ['Semua fitur Plus +', 'AI CS WhatsApp', 'Laporan otomatis ke WA', 'AI kasih peringatan duluan', 'Pengguna & SKU Unlimited', 'Priority Support'],
  },
};

// ─── Upgrade Modal ───────────────────────────────────────────────
const UpgradeModal = ({ requiredTier, featureName, onClose }) => {
  const [payError, setPayError] = useState('');
  const [planState, setPlanState] = useState(null);   // dari /billing/plans
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [payFor, setPayFor] = useState(null);         // tier yang sedang dibayar

  const PLANS = [
    { id: 'standard', name: 'Standard' },
    { id: 'plus', name: 'Plus', badge: 'Populer' },
    { id: 'max', name: 'Max' },
  ];

  // Status paket dihitung SERVER (/billing/plans), bukan ditebak di UI - supaya
  // aturan "tidak bisa turun paket, tidak bisa beli ulang paket yang masih aktif"
  // konsisten dengan guard backend.
  useEffect(() => {
    let alive = true;
    billingAPI.getPlans()
      .then((res) => alive && setPlanState(res.data))
      .catch(() => alive && setPlanState(null))
      .finally(() => alive && setLoadingPlans(false));
    return () => { alive = false; };
  }, []);

  const stateOf = (tierId) => planState?.plans?.find((p) => p.tier === tierId) || null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col">
        {/* Header — selalu terlihat, tidak ikut scroll */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Upgrade Plan</h2>
            <p className="text-sm text-gray-500 mt-1">
              Fitur <strong>{featureName}</strong> membutuhkan minimal paket{' '}
              <strong className="text-emerald-700">{TIER_LABEL[requiredTier] || requiredTier}</strong>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0">
            <X size={20} />
          </button>
        </div>

        {payError && (
          <div className="mx-6 mt-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600 flex-shrink-0">
            {payError}
          </div>
        )}

        {/* Paket — 3 kolom sejajar, Standard | Plus | Max */}
        <div className="px-6 py-5 overflow-y-auto">
          {loadingPlans ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[0, 1, 2].map((i) => <div key={i} className="h-72 rounded-xl bg-gray-100 animate-pulse" />)}
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {PLANS.map((plan) => {
              const pf = PLAN_FEATURES[plan.id];
              const isPopular = plan.id === 'plus';
              const st = stateOf(plan.id);
              const state = st?.state || 'upgrade';
              const selectable = st ? st.selectable : true;
              const isCurrent = state === 'current' || state === 'renew';
              const isIncluded = state === 'included';

              const label =
                state === 'renew' ? `Perpanjang ${plan.name}`
                : state === 'current' ? 'Paket kamu sekarang'
                : state === 'included' ? 'Sudah termasuk'
                : `Upgrade ke ${plan.name}`;

              return (
                <div
                  key={plan.id}
                  className={`p-4 rounded-xl border flex flex-col transition-opacity ${
                    isCurrent ? 'border-emerald-400 bg-emerald-50/60'
                    : isIncluded ? 'border-gray-200 bg-gray-50 opacity-60'
                    : isPopular ? 'border-emerald-300 bg-emerald-50/40' : 'border-gray-200'
                  }`}
                >
                  {isCurrent ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white w-fit mb-2">Paket kamu</span>
                  ) : plan.badge && !isIncluded ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 w-fit mb-2">{plan.badge}</span>
                  ) : (
                    <span className="text-[10px] mb-2">&nbsp;</span>
                  )}
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{pf.tagline}</p>
                  <p className="font-semibold text-gray-900 text-base">{plan.name}</p>
                  <p className="text-sm text-gray-500 mb-3">Rp {pf.price}<span className="text-xs">/bulan</span></p>

                  <div className="flex items-center gap-1.5 mb-3 bg-white border border-emerald-100 rounded-lg px-2.5 py-1.5 w-fit">
                    <Zap size={12} className="text-emerald-600" />
                    <span className="text-xs font-semibold text-emerald-700">{pf.credits.toLocaleString('id-ID')} Kredit AI/bulan</span>
                  </div>

                  <ul className="space-y-1.5 mb-4 flex-1">
                    {pf.features.map((f) => (
                      <li key={f} className="flex items-start gap-1.5 text-xs text-gray-600">
                        <Check size={12} className="mt-0.5 flex-shrink-0 text-emerald-600" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => { setPayError(''); setPayFor(plan.id); }}
                    disabled={!selectable}
                    title={!selectable ? (st?.reason || '') : ''}
                    className={`w-full px-4 py-2 text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
                      selectable
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {label}
                  </button>
                  {!selectable && st?.reason && (
                    <p className="text-[11px] text-gray-400 text-center mt-1.5">{st.reason}</p>
                  )}
                </div>
              );
            })}
          </div>
          )}
          <p className="text-xs text-gray-400 text-center mt-5">
            {planState?.current_tier && planState.current_tier !== 'trial'
              ? 'Mau turun paket atau ganti siklus? '
              : 'Pembayaran aman langsung di sini, QRIS, Virtual Account, e-wallet. '}
            <a href="https://wa.me/6285212546871" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
              Hubungi kami
            </a>
          </p>
        </div>
      </div>

      {payFor && (
        <PaymentSheet
          kind="subscription"
          tier={payFor}
          title={`Langganan Stockara ${payFor.charAt(0).toUpperCase() + payFor.slice(1)}`}
          subtitle="Pilih cara bayar yang paling gampang buat kamu"
          onClose={() => setPayFor(null)}
          onPaid={() => { setTimeout(() => window.location.reload(), 1200); }}
        />
      )}
    </div>
  );
};


// Click sound - iPhone keyboard style
const playClick = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.04, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const t = i / ctx.sampleRate;
      const env = Math.exp(-t * 120);
      data[i] = (Math.random() * 2 - 1) * env * 0.3 +
                Math.sin(2 * Math.PI * 1200 * t) * env * 0.15 +
                Math.sin(2 * Math.PI * 2400 * t) * env * 0.05;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.4, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.04);
    src.connect(g);
    g.connect(ctx.destination);
    src.start();
  } catch(e) {}
};

// ─── Main Layout ─────────────────────────────────────────────────
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [workspace, setWorkspace] = useState(() => localStorage.getItem('stockara_workspace') || 'all');
  const [expandedMenus, setExpandedMenus] = useState([]);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnnIds, setDismissedAnnIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dismissed_announcements') || '[]'); } catch { return []; }
  });
  const [upgradeModal, setUpgradeModal] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notifRef = useRef(null);
  const location = useLocation();
  const isChatMode = location.pathname.startsWith('/ai-chat');
  const navigate = useNavigate();
  const { user, logout, subscription, getTrialDaysRemaining, industry } = useAuth();
  const showWorkspaceSwitcher = isTestingAccount(user);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const res = await import('../services/api').then(m => m.superAdminAPI.getActiveAnnouncements());
        setAnnouncements(Array.isArray(res.data) ? res.data : []);
      } catch { setAnnouncements([]); }
    };
    fetchAnnouncements();
  }, []);

  useEffect(() => {
    const pathWorkspace = workspaceFromPath(location.pathname);
    if (pathWorkspace && pathWorkspace !== workspace) {
      setWorkspace(pathWorkspace);
      localStorage.setItem('stockara_workspace', pathWorkspace);
    }
  }, [location.pathname, workspace]);

  const chooseWorkspace = (nextWorkspace) => {
    setWorkspace(nextWorkspace);
    localStorage.setItem('stockara_workspace', nextWorkspace);
  };

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const res = await notificationAPI.getAll();
        setNotifications(res.data || []);
      } catch {}
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/ai/config');
        if (res.data?.timezone) setTenantTZ(res.data.timezone);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    if (notifOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen]);

  const handleMarkRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await Promise.all(
        notifications.filter(n => !n.is_read).map(n => notificationAPI.markRead(n.id))
      );
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  const daysRemaining = getTrialDaysRemaining();

  const toggleSubmenu = (id) => {
    setExpandedMenus(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const getSubBadge = () => {
    if (!subscription) return null;
    const { tier, status } = subscription;
    const dr = subscription.days_remaining;
    if (status === 'expired') return { label: 'Expired - aktifkan lagi', cls: 'bg-red-100 text-red-700 border-red-200' };
    if (tier === 'trial') return { label: `Trial - ${dr ?? '?'} hari`, cls: 'bg-amber-100 text-amber-700 border-amber-200' };
    if (tier === 'standard') return { label: 'Standard', cls: 'bg-green-100 text-green-700 border-green-200' };
    if (tier === 'plus') return { label: 'Plus', cls: 'bg-blue-100 text-blue-700 border-blue-200' };
    if (tier === 'max') return { label: 'Max', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    if (status === 'expired') return { label: '🔴 Expired, Aktifkan', cls: 'bg-red-100 text-red-700 border-red-200' };
    if (tier === 'trial') return { label: `🟡 Free Trial · ${dr ?? '?'} hari`, cls: 'bg-amber-100 text-amber-700 border-amber-200' };
    if (tier === 'standard') return { label: '🟢 Standard', cls: 'bg-green-100 text-green-700 border-green-200' };
    if (tier === 'plus') return { label: '🔵 Plus', cls: 'bg-blue-100 text-blue-700 border-blue-200' };
    if (tier === 'max') return { label: '⭐ Max', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    return null;
  };

  const handleGatedClick = (item) => {
    const requiredTier = TIER_REQUIRED[item.feature] || 'standard';
    setUpgradeModal({ requiredTier, featureName: item.label });
  };

  const subBadge = getSubBadge();

  return (
    <div className="stockara-app flex h-screen overflow-hidden bg-[#F7F3EC]">
      {upgradeModal && (
        <UpgradeModal
          requiredTier={upgradeModal.requiredTier}
          featureName={upgradeModal.featureName}
          onClose={() => setUpgradeModal(null)}
        />
      )}

      {mobileOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar - disembunyikan di mode Chat Bisnis (chat punya sidebar sendiri) */}
      {!isChatMode && (
      <aside className={`stockara-sidebar fixed lg:static inset-y-0 left-0 z-50 flex flex-col transition-all duration-300 ease-in-out bg-[#FCFAF6] text-slate-800 border-r border-emerald-900/10 shadow-[12px_0_40px_rgba(26,87,51,0.04)] ${sidebarOpen ? 'w-[272px]' : 'w-20'} ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Switcher */}
        <div className="px-5 pt-4 pb-3 border-b border-emerald-900/10">
          {sidebarOpen ? (
            <ModeSwitcher isChatMode={isChatMode} />
          ) : (
            <ModeSwitcher isChatMode={isChatMode} compact />
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-4 scrollbar-thin">
          {sidebarOpen && showWorkspaceSwitcher && (
            <div className="mb-4">
              <p className="px-1 mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">Ruang Kerja</p>
              <div className="grid grid-cols-2 gap-2">
                {WORKSPACES.map((space) => {
                  const active = normalizeIndustry(workspace) === space.id;
                  const Icon = space.icon;
                  return (
                    <button
                      key={space.id}
                      type="button"
                      onClick={() => { playClick(); chooseWorkspace(space.id); }}
                      className={`rounded-xl border px-2.5 py-2.5 text-left transition-all ${active ? 'border-emerald-300 bg-emerald-50 text-emerald-800 shadow-sm' : 'border-emerald-900/10 bg-white/70 text-slate-500 hover:border-emerald-200 hover:bg-white hover:text-slate-900'}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={14} />
                        <span className="text-xs font-bold">{space.label}</span>
                      </div>
                      <span className="block mt-0.5 text-[10px] text-slate-400">{space.hint}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {visibleFor(user, industry, workspace).map((item) => {
            const accessible = true;
            const activeClass = isActive(item.path) ? 'bg-[#DFF3EA] text-[#0B6B43] shadow-sm' : 'text-slate-600 hover:bg-white hover:text-slate-950';
            const lockedClass = 'text-slate-300 hover:bg-white cursor-pointer';
            const base = 'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200';

            return (
              <div key={item.id} className="mb-1">
                {item.children ? (
                  <>
                    <button
                      onClick={() => { playClick(); accessible ? toggleSubmenu(item.id) : handleGatedClick(item); }}
                      className={`${base} ${accessible ? activeClass : lockedClass}`}
                    >
                      <item.icon size={20} className="flex-shrink-0" />
                      {sidebarOpen && (
                        <>
                          <span className="flex-1 text-left">{item.label}</span>
                          {!accessible
                            ? <Lock size={14} className="flex-shrink-0 text-gray-300" />
                            : expandedMenus.includes(item.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                          }
                        </>
                      )}
                    </button>
                    {accessible && sidebarOpen && expandedMenus.includes(item.id) && (
                      <div className="ml-9 mt-1 space-y-1">
                        {item.children.map((child) => (
                          <Link key={child.path} to={child.path} onClick={playClick}
                            className={`block px-3 py-2 rounded-lg text-sm transition-all duration-200 ${location.pathname === child.path ? 'text-[#0B6B43] bg-emerald-50 font-semibold' : 'text-slate-500 hover:text-slate-900 hover:bg-white'}`}>
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : accessible ? (
                  <Link to={item.path} onClick={playClick} className={`${base} ${activeClass}`}>
                    <item.icon size={20} className="flex-shrink-0" />
                    {sidebarOpen && <span>{item.label}</span>}
                  </Link>
                ) : (
                  <button onClick={() => { playClick(); handleGatedClick(item); }} className={`${base} ${lockedClass} text-left`}>
                    <item.icon size={20} className="flex-shrink-0" />
                    {sidebarOpen && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        <Lock size={14} className="flex-shrink-0 text-gray-300" />
                      </>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </nav>

        {/* Sidebar footer */}
        <div className="p-4 border-t border-emerald-900/10 space-y-2 bg-white/45">
          {sidebarOpen && subscription && (
            <button
              onClick={() => { playClick(); setUpgradeModal({ requiredTier: 'standard', featureName: 'semua fitur premium' }); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-[#0B6B43] text-xs font-bold hover:bg-emerald-100 transition-all"
            >
              <Zap size={14} />
              <span>Upgrade Plan</span>
              <span className="ml-auto text-gray-500 capitalize">{subscription.tier}</span>
            </button>
          )}
          <button
            onClick={() => { playClick(); setSidebarOpen(!sidebarOpen); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-500 hover:text-slate-900 hover:bg-white transition-all"
          >
            <Menu size={20} />
            {sidebarOpen && <span>Tutup Sidebar</span>}
          </button>
        </div>
      </aside>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {!isChatMode && !bannerDismissed && (
          <TrialBanner
            days={daysRemaining}
            onUpgrade={() => setUpgradeModal({ requiredTier: 'standard', featureName: 'semua fitur premium' })}
            onDismiss={() => setBannerDismissed(true)}
          />
        )}
        {!isChatMode && (
          <>

        {/* Announcement banners */}
        {announcements.filter(a => !dismissedAnnIds.includes(a.id)).map(ann => {
          const colorMap = {
            info: 'bg-blue-50 border-blue-200 text-blue-800',
            warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
            success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
            error: 'bg-red-50 border-red-200 text-red-800',
          };
          const cls = colorMap[ann.type] || colorMap.info;
          return (
            <div key={ann.id} className={`flex items-start justify-between gap-3 px-4 py-2.5 border-b text-sm ${cls}`}>
              <div className='flex items-start gap-2'>
                <span className='text-base leading-none mt-0.5'>&#128226;</span>
                <div>
                  <span className='font-semibold'>{ann.title}</span>
                  {ann.message && <span className='ml-2 opacity-80'>{ann.message}</span>}
                </div>
              </div>
              {ann.dismissable !== false && (
                <button
                  onClick={() => {
                    const next = [...dismissedAnnIds, ann.id];
                    setDismissedAnnIds(next);
                    try { localStorage.setItem('dismissed_announcements', JSON.stringify(next)); } catch {}
                  }}
                  className='flex-shrink-0 text-lg leading-none opacity-60 hover:opacity-100 mt-0.5'
                >&times;</button>
              )}
            </div>
          );
        })}

        {/* Top bar */}
        <header className="stockara-topbar h-[72px] bg-[#FCFAF6]/95 backdrop-blur border-b border-emerald-900/10 flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
          <div className="flex items-center gap-4">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden rounded-xl p-2 text-slate-600 hover:bg-white hover:text-slate-900">
              <Menu size={20} />
            </button>
            <div className="relative hidden sm:block">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Cari produk, transaksi, laporan..."
                className="pl-10 pr-4 py-2.5 w-80 bg-white border border-emerald-900/10 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            {subBadge && (
              <button
                onClick={() => setUpgradeModal({ requiredTier: 'standard', featureName: 'upgrade plan' })}
                className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all hover:opacity-80 ${subBadge.cls}`}
              >
                {subBadge.label}
              </button>
            )}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen(o => !o)}
                className="relative rounded-xl p-2 text-slate-600 transition-all hover:bg-white hover:text-slate-900"
              >
                <Bell size={20} />
                {notifications.filter(n => !n.is_read).length > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 px-0.5 bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
                    {notifications.filter(n => !n.is_read).length > 9 ? '9+' : notifications.filter(n => !n.is_read).length}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="notif-dropdown absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-emerald-900/10 bg-[#FFFCF7] shadow-2xl">
                  <div className="flex items-center justify-between border-b border-emerald-900/10 px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900">Notifikasi</p>
                    {notifications.filter(n => !n.is_read).length > 0 && (
                      <button onClick={handleMarkAllRead} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">
                        Tandai semua dibaca
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="py-10 text-center">
                        <Bell size={28} className="text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">Belum ada notifikasi</p>
                      </div>
                    ) : (
                      notifications.map(n => {
                        const typeClr = { info: 'bg-blue-100 text-blue-600', warning: 'bg-amber-100 text-amber-600', success: 'bg-emerald-100 text-emerald-600' };
                        const clr = typeClr[n.notification_type] || typeClr.info;
                        return (
                          <div
                            key={n.id}
                            onClick={() => !n.is_read && handleMarkRead(n.id)}
                            className={`px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-all ${n.is_read ? 'opacity-60' : ''}`}
                          >
                            <div className="flex items-start gap-3">
                              <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${n.is_read ? 'bg-gray-300' : 'bg-emerald-500'}`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{n.title}</p>
                                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                                <p className="text-[10px] text-gray-400 mt-1">
                                  {fmtDatetime(n.created_at, '-')}
                                </p>
                              </div>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${clr}`}>
                                {n.notification_type === 'warning' ? 'Peringatan' : n.notification_type === 'success' ? 'Sukses' : 'Info'}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
            <Link to="/settings" className="rounded-xl p-2 text-slate-600 transition-all hover:bg-white hover:text-slate-900">
              <Settings size={20} />
            </Link>
            <div className="flex items-center gap-3 pl-3 border-l border-emerald-900/10">
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#0B6B43] text-sm font-bold text-white">
                  {user?.name?.charAt(0) || 'U'}
                </div>
              )}
              <div className="hidden md:block">
                <p className="text-sm font-semibold text-slate-900">{user?.name || 'User'}</p>
                <p className="text-xs text-slate-500">{user?.role || ''}</p>
              </div>
              <button onClick={logout} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all" title="Logout">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </header>
          </>
        )}

        {/* Page content */}
        <main className={isChatMode ? 'flex-1 overflow-hidden' : 'stockara-page-shell flex-1 overflow-y-auto p-4 sm:p-5 lg:p-7'}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
